import { Link, useParams } from 'react-router-dom'

import type { ActivityEntry } from '@/api/automation'
import { NotLivePage, useNotLivePage } from '@/app/notLive'
import { Card, DataTable, EmptyState, formatInteger, formatRelativeTime, LoadingBlock, useNow, type Column } from '@/design'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import './automation.css'
import { ActivityOutcomeBadge, AutomationStatusBadge } from './badges'
import { useAutomationQuery, useSetWorkflowStatus } from './hooks'

function activityColumns(now: Date): Column<ActivityEntry>[] {
  return [
    { key: 'at', header: 'When', sortValue: (row) => row.at, cell: (row) => formatRelativeTime(row.at, now) },
    { key: 'outcome', header: 'Outcome', cell: (row) => <ActivityOutcomeBadge outcome={row.outcome} /> },
    { key: 'summary', header: 'Summary', cell: (row) => row.summary },
  ]
}

/** One workflow's trigger, conditions and actions, with a pause/resume control and its recent run history. */
export function WorkflowDetailPage() {
  const notLive = useNotLivePage('workflow')
  const { id } = useParams<{ id: string }>()
  const workflows = useAutomationQuery('workflows', (client) => client.workflows())
  const activity = useAutomationQuery('activity', (client) => client.automationActivity())
  const setStatus = useSetWorkflowStatus()
  const search = useFilterSearch()
  const now = useNow()

  const workflow = workflows.data?.data.find((candidate) => candidate.id === id)
  const runHistory = (activity.data?.data ?? []).filter((entry) => entry.workflowId === id)

  if (notLive) return <NotLivePage />

  if (workflows.isPending) return <LoadingBlock rows={6} />

  if (workflows.isError) {
    return (
      <Card>
        <EmptyState variant="outage" description="This workflow could not be loaded." />
      </Card>
    )
  }

  if (!workflow) {
    return (
      <Card>
        <EmptyState
          variant="no-results"
          title="Workflow not found"
          description="It may have been removed, or the link is out of date."
          action={
            <Link className="ci-button" to={{ pathname: '/automation/workflows', search }}>
              Back to workflows
            </Link>
          }
        />
      </Card>
    )
  }

  const toggleStatus = () =>
    setStatus.mutate({ id: workflow.id, status: workflow.status === 'running' ? 'paused' : 'running' })

  return (
    <div className="ci-stack">
      <Card
        title={workflow.name}
        subtitle={`Created by ${workflow.createdBy}`}
        actions={<AutomationStatusBadge status={workflow.status} />}
        footer={
          workflow.status !== 'draft' ? (
            <button
              type="button"
              className="ci-button"
              data-variant={workflow.status === 'running' ? 'ghost' : 'primary'}
              disabled={setStatus.isPending}
              onClick={toggleStatus}
            >
              {workflow.status === 'running' ? 'Pause' : 'Resume'}
            </button>
          ) : null
        }
      >
        <dl className="au-summary">
          <div>
            <dt>Trigger</dt>
            <dd>{workflow.trigger.detail}</dd>
          </div>
          {workflow.conditions.length > 0 ? (
            <div>
              <dt>Conditions</dt>
              <dd>{workflow.conditions.map((condition) => `${condition.label} ${condition.operator} ${condition.value}`).join('; ')}</dd>
            </div>
          ) : null}
          <div>
            <dt>Actions</dt>
            <dd>{workflow.actions.map((action) => action.label).join(', ')}</dd>
          </div>
          <div>
            <dt>Runs</dt>
            <dd>{formatInteger(workflow.runCount)}</dd>
          </div>
        </dl>
      </Card>

      <Card title="Run history" subtitle="What this workflow has done recently.">
        {activity.isPending ? <LoadingBlock rows={4} /> : null}
        {activity.isError ? <EmptyState variant="outage" compact description="Run history could not be loaded." /> : null}
        {activity.data ? (
          <DataTable
            caption={`${workflow.name} run history`}
            columns={activityColumns(now)}
            rows={runHistory}
            rowKey={(row) => row.id}
            initialSort={{ key: 'at', direction: 'desc' }}
            empty={<EmptyState variant="no-data" compact title="No runs yet" />}
          />
        ) : null}
      </Card>
    </div>
  )
}
